import { pluckI, subject, withInitialValue } from '@noshiro/syncflow';
import { IList, IMap } from '@noshiro/ts-utils';
/**
 * @description Creates pathname Observable and queryParams Observable,
 * and starts listening URL changes.
 */
export const createRouter = () => {
    const pathnameAndQueryParamsSource$ = subject();
    const pathnameAndQueryParams$ = pathnameAndQueryParamsSource$.chain(withInitialValue({
        pathname: withSlash(window.location.pathname),
        queryParams: parseQueryParamsStr(window.location.search),
    }));
    const pathname$ = pathnameAndQueryParams$.chain(pluckI('pathname'));
    const queryParams$ = pathnameAndQueryParams$.chain(pluckI('queryParams'));
    const updatePathname = () => {
        pathnameAndQueryParamsSource$.next({
            pathname: withSlash(window.location.pathname),
            queryParams: parseQueryParamsStr(window.location.search),
        });
    };
    const push = (nextPath) => {
        const p = withSlash(nextPath);
        window.history.pushState({}, '', p);
        updatePathname();
    };
    const updateQueryParams = (recipe, pushState = true, sortParams = true) => {
        const searchParams = new URLSearchParams(window.location.search);
        recipe(searchParams);
        if (sortParams) {
            searchParams.sort();
        }
        const p = searchParams.toString() === ''
            ? withSlash(window.location.pathname)
            : [withSlash(window.location.pathname), searchParams.toString()].join('?');
        if (pushState) {
            window.history.pushState({}, '', p);
        }
        else {
            window.history.replaceState({}, '', p);
        }
        updatePathname();
    };
    const back = () => {
        window.history.back();
        updatePathname();
    };
    const redirect = (nextPath) => {
        const p = withSlash(nextPath);
        window.history.replaceState({}, '', p);
        updatePathname();
    };
    window.addEventListener('popstate', updatePathname);
    return {
        push,
        updateQueryParams,
        back,
        redirect,
        pathname$,
        queryParams$,
        removeListener: () => {
            window.removeEventListener('popstate', updatePathname);
        },
    };
};
const validateKeyValuePairs = (kvs) => kvs.every(IList.isArrayOfLength2);
const parseQueryParamsStr = (queryParamsStr) => {
    if (!queryParamsStr.startsWith('?')) {
        return IMap.new([]);
    }
    const keyValuePairs = queryParamsStr
        .slice(1) // remove "?"
        .split('&') // "key1=value1&key2=value2" -> ["key1=value1", "key2=value2"]
        .map((group) => group.split('=')); // "key1=value1" -> ["key1", "value1"]
    if (!validateKeyValuePairs(keyValuePairs)) {
        return IMap.new([]);
    }
    return IMap.new(keyValuePairs);
};
export const withSlash = (path) => path.endsWith('/') ? path : `${path}/`;
